import React, { useState, useEffect, useRef } from 'react';
import { Alert, InputGroup } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import List from 'list.js';
import { useTranslation } from 'react-i18next';
import Modal from '../../components/Modal';
import { CenteredLoader } from '../../components/LoadingSpinner';
import { useCustomers, useCreateCustomer, useUpdateCustomer, useDeleteCustomer } from '../../entities/customer';
import { readListJsSort, attachListJsSortPersistence } from '../../shared/useSortPreference';
import EmptyState from '../../components/feedback/EmptyState';
import PageSection from '../../components/layout/PageSection';
import PageShell from '../../components/layout/PageShell';
import { usePermissions } from '../../shared/permissions';
import AppButton from '../../components/ui/AppButton';
import AppFormGroup from '../../components/ui/AppFormGroup';
import { IconPlus, IconSearch } from '@tabler/icons-react';

function Customers() {
  const { t } = useTranslation();
  const { canManage } = usePermissions();

  const [formErrors, setFormErrors] = useState(null);
  const [customerToEdit, setCustomerToEdit] = useState(null);
  const [customerToDelete, setCustomerToDelete] = useState(null);

  // Modal state management
  const [newCustomerShow, setNewCustomerShow] = useState(false);
  const [editCustomerShow, setEditCustomerShow] = useState(false);
  const [deleteCustomerShow, setDeleteCustomerShow] = useState(false);
  const [aresLoading, setAresLoading] = useState(false);
  const [aresError, setAresError] = useState(null);

  const [newCustomer, setNewCustomer] = useState({
    company_name: '',
    company_id: '',
    resp_person: '',
    street: '',
    city: '',
    postal_code: '',
    tax_id: '',
    telephone: '',
    mobile: '',
    email: '',
    discount: 0,
    description: ''
  });

  const listInstance = useRef(null);
  const tableRef = useRef(null);

  // ── Server state ──────────────────────────────────────────────────────────
  const {
    data: customers = [],
    isLoading,
    error,
  } = useCustomers();

  const createCustomer = useCreateCustomer();
  const createMutation = {
    isPending: createCustomer.isPending,
    mutate: (data) => createCustomer.mutate(data, {
      onSuccess: handleCloseNewCustomer,
      onError: (err) => setFormErrors({ error: err.message || t('customers.errors.failedToCreate') }),
    }),
  };

  const updateCustomer = useUpdateCustomer();
  const updateMutation = {
    isPending: updateCustomer.isPending,
    mutate: (data) => updateCustomer.mutate({ id: data.id, data }, {
      onSuccess: handleCloseEditCustomer,
      onError: (err) => setFormErrors({ error: err.message || t('customers.errors.failedToUpdate') }),
    }),
  };

  const deleteCustomer = useDeleteCustomer();
  const deleteMutation = {
    isPending: deleteCustomer.isPending,
    mutate: (id) => deleteCustomer.mutate(id, {
      onSuccess: handleCloseDeleteCustomer,
      onError: (err) => {
        setFormErrors({ error: err.message || t('customers.errors.failedToDelete') });
        handleCloseDeleteCustomer();
      },
    }),
  };

  // Modal handlers
  const handleCloseNewCustomer = () => {
    setNewCustomerShow(false);
    setFormErrors(null);
  };

  const handleShowNewCustomer = () => {
    setFormErrors(null);
    setNewCustomer({
      company_name: '',
      company_id: '',
      resp_person: '',
      street: '',
      city: '',
      postal_code: '',
      tax_id: '',
      telephone: '',
      mobile: '',
      email: '',
      discount: 0,
      description: ''
    });
    setNewCustomerShow(true);
  };

  const handleCloseEditCustomer = () => {
    setEditCustomerShow(false);
    setCustomerToEdit(null);
    setFormErrors(null);
  };

  const handleShowEditCustomer = (customer) => {
    setCustomerToEdit({ ...customer });
    setFormErrors(null);
    setEditCustomerShow(true);
  };

  const handleCloseDeleteCustomer = () => {
    setDeleteCustomerShow(false);
    setCustomerToDelete(null);
  };

  const handleShowDeleteCustomer = (customer) => {
    setCustomerToDelete(customer);
    setDeleteCustomerShow(true);
  };

  useEffect(() => {
    // Initialize list.js for searching, sorting and pagination
    if (!isLoading && customers.length > 0 && tableRef.current) {
      listInstance.current = new List('table-default', {
        sortClass: 'table-sort',
        listClass: 'table-tbody',
        valueNames: ['company_name', 'company_id', 'resp_person', 'city', 'telephone', 'email', 'discount', 'description'],
        page: 10,
        pagination: [{
          paginationClass: "pagination",
          innerWindow: 3,
          outerWindow: 2
        }]
      });

      // Restore saved sort state
      const saved = readListJsSort('customers');
      if (saved?.col) {
        listInstance.current.sort(saved.col, { order: saved.order || 'asc' });
      }

      // Persist sort state on each click
      const cleanupSort = attachListJsSortPersistence('customers', tableRef);

      return () => {
        cleanupSort();
        if (listInstance.current) {
          // Clean up by removing event listeners instead of using destroy
          // Some versions of list.js don't have a destroy method
          try {
            // If destroy exists, use it
            if (typeof listInstance.current.destroy === 'function') {
              listInstance.current.destroy();
            } else {
              // Otherwise just set it to null
              listInstance.current = null;
            }
          } catch (error) {
            console.warn('Error cleaning up list.js instance:', error);
            listInstance.current = null;
          }
        }
      };
    }
  }, [isLoading, customers]);

  const handleSearchInput = (e) => {
    if (listInstance.current) {
      listInstance.current.search(e.target.value);
    }
  };

  const handleNewCustomerChange = (e) => {
    const { name, value } = e.target;
    setNewCustomer(prev => ({ ...prev, [name]: value }));
  };

  const fetchFromAres = async (ico, isEdit) => {
    if (!ico) return;
    setAresLoading(true);
    setAresError(null);
    try {
      const res = await fetch(`https://ares.gov.cz/ekonomicke-subjekty-v-be/rest/ekonomicke-subjekty/${encodeURIComponent(ico)}`);
      if (!res.ok) throw new Error('not found');
      const data = await res.json();
      const sidlo = data.sidlo || {};
      const streetParts = [sidlo.nazevUlice, sidlo.cisloDomovni, sidlo.cisloOrientacni].filter(Boolean);
      const setter = isEdit ? setCustomerToEdit : setNewCustomer;
      setter(prev => ({
        ...prev,
        company_name: data.obchodniJmeno || prev.company_name,
        tax_id: data.dic || prev.tax_id,
        street: streetParts.length ? streetParts.join(' ') : prev.street,
        city: sidlo.nazevObce || prev.city,
        postal_code: sidlo.psc ? String(sidlo.psc) : prev.postal_code,
      }));
    } catch {
      setAresError(t('customers.aresError'));
    } finally {
      setAresLoading(false);
    }
  };


  const handleEditCustomerChange = (e) => {
    const { name, value } = e.target;
    setCustomerToEdit(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitNewCustomer = async (e) => {
    e.preventDefault();
    setFormErrors(null);
    createMutation.mutate(newCustomer);
  };

  const handleSubmitEditCustomer = async (e) => {
    e.preventDefault();
    setFormErrors(null);
    updateMutation.mutate(customerToEdit);
  };

  const handleDeleteCustomer = (id) => {
    deleteMutation.mutate(id);
  };

  const renderCustomerForm = (customer = null, isEdit = false) => {
    const data = customer || newCustomer;
    const handleChange = isEdit ? handleEditCustomerChange : handleNewCustomerChange;

    return (
      <>
        <div className="mb-3">
          <label className="form-label">{t('customers.form.companyName')}</label>
          <input
            type="text"
            name="company_name"
            className="form-control"
            value={data.company_name}
            onChange={handleChange}
            required
          />
        </div>
        <div className="mb-3">
          <label className="form-label">{t('customers.form.companyId')}</label>
          <div className="input-group">
            <input
              type="text"
              name="company_id"
              className="form-control"
              value={data.company_id}
              onChange={handleChange}
              required
            />
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => fetchFromAres(data.company_id, isEdit)}
              disabled={aresLoading || !data.company_id}
            >
              {aresLoading ? (
                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              ) : null}
              {t('customers.aresButton')}
            </button>
          </div>
          {aresError && (
            <div className="text-danger small mt-1">{aresError}</div>
          )}
        </div>
        <div className="mb-3">
          <label className="form-label">{t('customers.form.respPerson')}</label>
          <input
            type="text"
            name="resp_person"
            className="form-control"
            value={data.resp_person}
            onChange={handleChange}
            required
          />
        </div>
        <div className="mb-3">
          <label className="form-label">{t('customers.form.street')}</label>
          <input
            type="text"
            name="street"
            className="form-control"
            value={data.street}
            onChange={handleChange}
            required
          />
        </div>
        <div className="mb-3">
          <label className="form-label">{t('customers.form.city')}</label>
          <input
            type="text"
            name="city"
            className="form-control"
            value={data.city}
            onChange={handleChange}
            required
          />
        </div>
        <div className="mb-3">
          <label className="form-label">{t('customers.form.postalCode')}</label>
          <input
            type="text"
            name="postal_code"
            className="form-control"
            value={data.postal_code}
            onChange={handleChange}
            required
          />
        </div>
        <div className="mb-3">
          <label className="form-label">{t('customers.form.taxId')}</label>
          <input
            type="text"
            name="tax_id"
            className="form-control"
            value={data.tax_id}
            onChange={handleChange}
          />
        </div>
        <div className="mb-3">
          <label className="form-label">{t('customers.form.telephone')}</label>
          <input
            type="text"
            name="telephone"
            className="form-control"
            value={data.telephone}
            onChange={handleChange}
          />
        </div>
        <div className="mb-3">
          <label className="form-label">{t('customers.form.mobile')}</label>
          <input
            type="text"
            name="mobile"
            className="form-control"
            value={data.mobile}
            onChange={handleChange}
          />
        </div>
        <div className="mb-3">
          <label className="form-label">{t('customers.form.email')}</label>
          <input
            type="email"
            name="email"
            className="form-control"
            value={data.email}
            onChange={handleChange}
          />
        </div>
        <div className="mb-3">
          <label className="form-label">{t('customers.form.discount')}</label>
          <input
            type="number"
            name="discount"
            className="form-control"
            value={data.discount}
            onChange={handleChange}
            step="any"
            min="0"
          />
        </div>
        <div className="mb-3">
          <label className="form-label">{t('customers.form.description')}</label>
          <textarea
            name="description"
            className="form-control"
            value={data.description}
            onChange={handleChange}
          ></textarea>
        </div>
      </>
    );
  };

  return (
    <PageShell
      pretitle={t('customers.overview')}
      title={t('customers.title')}
      description={t('customers.subtitle', 'Customer directory with quick editing, lookup and communication context.')}
      actions={canManage ? (
        <AppButton variant="primary" icon={<IconPlus size={16} />} onClick={handleShowNewCustomer}>
          {t('customers.newCustomer')}
        </AppButton>
      ) : null}
    >
      {formErrors && (
        <Alert variant="danger" className="app-inline-alert">
          {typeof formErrors === 'string'
            ? formErrors
            : Object.entries(formErrors).map(([key, value]) => (
                <div key={key}><strong>{key}:</strong> {value}</div>
              ))}
        </Alert>
      )}

      {error && (
        <Alert variant="danger" className="app-inline-alert">{error.message || t('customers.errors.failedToLoad')}</Alert>
      )}

      <PageSection
        title={t('customers.title')}
        description={t('customers.sectionDescription', 'Search and maintain your customer base from one place.')}
        actions={
          <InputGroup className="app-search">
            <InputGroup.Text><IconSearch size={16} /></InputGroup.Text>
            <input
              type="text"
              id="table-search"
              className="form-control"
              placeholder={t('customers.searchPlaceholder')}
              onChange={handleSearchInput}
            />
          </InputGroup>
        }
        bodyClassName="p-0"
      >
        {isLoading ? (
          <div className="p-4">
            <CenteredLoader message={t('customers.loading')} />
          </div>
        ) : customers.length === 0 ? (
          <EmptyState
            title={t('customers.noCustomersFound')}
            message={t('customers.emptyMessage', 'Add your first customer or import company data from ARES to build the directory faster.')}
            action={canManage ? (
              <AppButton variant="primary" icon={<IconPlus size={16} />} onClick={handleShowNewCustomer}>
                {t('customers.newCustomer')}
              </AppButton>
            ) : null}
          />
        ) : (
          <div id="table-default" className="table-responsive" ref={tableRef}>
            <table className="table table-hover app-table">
              <thead>
                <tr>
                  <th><button className="table-sort" data-sort="company_name">{t('customers.table.companyName')}</button></th>
                  <th><button className="table-sort" data-sort="company_id">{t('customers.table.companyId')}</button></th>
                  <th><button className="table-sort" data-sort="resp_person">{t('customers.table.respPerson')}</button></th>
                  <th><button className="table-sort" data-sort="city">{t('customers.table.city')}</button></th>
                  <th><button className="table-sort" data-sort="telephone">{t('customers.table.telephone')}</button></th>
                  <th><button className="table-sort" data-sort="email">{t('customers.table.email')}</button></th>
                  <th><button className="table-sort" data-sort="discount">{t('customers.table.discount')}</button></th>
                  <th><button className="table-sort" data-sort="description">{t('customers.table.description')}</button></th>
                  <th>{t('customers.table.actions')}</th>
                </tr>
              </thead>
              <tbody className="table-tbody">
                {customers.map(customer => (
                    <tr key={customer.id}>
                      <td className="company_name">
                        <Link to={`/customers/customer/${customer.id}`}>{customer.company_name}</Link>
                      </td>
                      <td className="company_id">{customer.company_id}</td>
                      <td className="resp_person">{customer.resp_person}</td>
                      <td className="city">{customer.city}</td>
                      <td className="telephone">{customer.telephone}</td>
                      <td className="email">{customer.email}</td>
                      <td className="discount">{customer.discount}</td>
                      <td className="description">{customer.description}</td>
                      <td>
                        {canManage && (
                          <div className="btn-group">
                            <AppButton
                              variant="outline-secondary"
                              size="sm"
                              icon={<i className="ti ti-edit" aria-hidden="true" />}
                              onClick={() => handleShowEditCustomer(customer)}
                              title={t('common.edit')}
                            />
                            <AppButton
                              variant="outline-danger"
                              size="sm"
                              icon={<i className="ti ti-trash" aria-hidden="true" />}
                              onClick={() => handleShowDeleteCustomer(customer)}
                              title={t('common.delete')}
                            />
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            <div className="app-table-footer">
              <ul className="pagination mb-0"></ul>
            </div>
          </div>
        )}
      </PageSection>

      {/* New Customer Modal */}
      <Modal
        show={newCustomerShow}
        onHide={handleCloseNewCustomer}
        title={t('customers.newCustomer')}
        size="lg"
        fullscreen="sm-down"
        footer={
          <>
            <AppButton variant="ghost" onClick={handleCloseNewCustomer}>{t('common.cancel')}</AppButton>
            <AppButton
              variant="primary"
              icon={<IconPlus size={16} />}
              className="ms-auto"
              onClick={handleSubmitNewCustomer}
              loading={createMutation.isPending}
              loadingText={t('common.saving')}
            >
              {t('customers.form.create')}
            </AppButton>
          </>
        }
      >
        {renderCustomerForm()}
      </Modal>

      {/* Edit Customer Modal */}
      <Modal
        show={editCustomerShow}
        onHide={handleCloseEditCustomer}
        title={t('customers.form.edit')}
        size="lg"
        fullscreen="sm-down"
        footer={
          <>
            <AppButton variant="ghost" onClick={handleCloseEditCustomer}>{t('common.cancel')}</AppButton>
            <AppButton
              variant="primary"
              onClick={handleSubmitEditCustomer}
              loading={updateMutation.isPending}
              loadingText={t('common.saving')}
            >
              {t('customers.form.saveChanges')}
            </AppButton>
          </>
        }
      >
        {customerToEdit && renderCustomerForm(customerToEdit, true)}
      </Modal>

      {/* Delete Customer Modal */}
      <Modal
        show={deleteCustomerShow}
        onHide={handleCloseDeleteCustomer}
        title={t('common.confirm')}
        footer={
          <>
            <AppButton variant="ghost" onClick={handleCloseDeleteCustomer}>{t('common.cancel')}</AppButton>
            <AppButton
              variant="danger"
              onClick={() => handleDeleteCustomer(customerToDelete?.id)}
              loading={deleteMutation.isPending}
              loadingText={t('common.deleting')}
            >
              {t('common.delete')}
            </AppButton>
          </>
        }
      >
        <p>
          {customerToDelete && t('customers.form.confirmDelete', { name: customerToDelete.company_name })}
        </p>
      </Modal>
    </PageShell>
  );
}

export default Customers;
